import { useEffect, useState, type ReactNode } from "react";
import { DismissButton, ToastIcon, ToastTimer } from "../Notices";
import { Spinner } from "../Spinner";

export type SaveState = "idle" | "dirty" | "saved";

/** How long the Saved toast stays, with its Undo. */
const SAVED_MS = 15_000;

/**
 * Where a screen's changes stand.
 * - `dirty`: a bar at the foot of the screen with Discard and Save, while changes wait for Save.
 * - `saved`: a toast that says they're live now, with Undo; it goes after a while or when dismissed.
 * - `idle`: nothing to save; `message` says what saves as you go.
 */
export function SaveBar(props: { state: SaveState; message?: ReactNode; busy?: boolean; saveLabel?: string; onSave?: () => void; onDiscard?: () => void; onUndo?: () => void }) {
  const { state, message, busy, saveLabel = "Save", onSave, onDiscard } = props;
  if (state === "saved") return <SavedToast {...props} />;
  if (state === "dirty") {
    return <div className="bw-area-savebar" data-state="dirty" role="status" aria-busy={busy}>
      <span><i className="bw-area-dot" aria-hidden="true" /> {message ?? "Changes · not saved yet"}</span>
      {onDiscard && <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={onDiscard}>Discard</button>}
      <button type="button" className="bw-btn bw-btn--primary bw-btn--action" disabled={busy} aria-busy={busy} onClick={onSave}>{busy && <Spinner />}{saveLabel}</button>
    </div>;
  }
  return <div className="bw-area-savebar" data-state="idle" role="status"><span>{message}</span></div>;
}

/** Each save (busy, then done) shows the toast again and restarts its timer. */
function SavedToast({ message, busy, onUndo }: { message?: ReactNode; busy?: boolean; onUndo?: () => void }) {
  const [round, setRound] = useState(0);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    if (busy) { setHidden(false); return; }
    setRound((value) => value + 1);
    const timer = setTimeout(() => setHidden(true), SAVED_MS);
    return () => clearTimeout(timer);
  }, [busy]);
  if (hidden) return null;
  return <div className="bw-toast" data-area-saved="" role="status" aria-busy={busy}>
    {busy ? <Spinner /> : <ToastIcon tone="done" />}
    <span className="bw-toast-text">{busy ? "Saving…" : message ?? "Saved · live now"}</span>
    {onUndo && <button type="button" className="bw-btn bw-toast-action" disabled={busy} onClick={onUndo}>Undo</button>}
    {!busy && <DismissButton onDismiss={() => setHidden(true)} />}
    {!busy && <ToastTimer key={round} ms={SAVED_MS} />}
  </div>;
}
